// noinspection TypeScriptPreferShortImport
import { SessionService } from '../session/session.service';
// noinspection TypeScriptPreferShortImport
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { defer, Observable, of, throwError } from 'rxjs';
import { catchError, concatAll, filter, mapTo, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { GigyaResponse } from '../../models';
import { CookieService } from 'ngx-cookie-service';
import { HttpParamsEncoder } from '../../utils/http-params-encoder';
import { DcEnvUtilsService } from '../dc-env-utils/dc-env-utils.service';

type RequestOptions = {
  body?: any;
  headers?: HttpHeaders | {
    [header: string]: string | string[];
  };
  observe?: 'body' | 'events' | 'response';
  params?: HttpParams | {
    [param: string]: string | string[];
  };
  responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
  reportProgress?: boolean;
  withCredentials?: boolean;
};

export declare type HttpMethod = 'DELETE' | 'GET' | 'POST' | 'PUT' | 'HEAD' | 'OPTIONS';

export type ApiServiceParams = {
  [key: string]: any,
  requestBody?: any,
  requestHeader?: any,
  queryParams?: any
};

const errorHandler = e => {

  // case of HTTP response code !== 200
  console.error(`[ApiService] errorCode ${e?.errorCode || e.error?.errorCode || 'N/A'} - "${e?.errorMessage || e.error?.errorMessage || e.statusText || 'Unknown Error'}" - "${e?.errorDetails || e.error?.errorDetails || e.error?.statusReason || 'Something went wrong...'}"`, { ...e });

  // normalize strange "Response" field (UcpSchemaService)
  if (e.error?.Response) {
    Object.assign(e.error, Object.keys(e.error.Response)
      .reduce((obj, key) => {
        const firstChar = key[0];
        const camelCaseKey = key.replace(firstChar, firstChar.toLowerCase());
        obj[camelCaseKey] = e.error.Response[key];
        return obj;
      }, {}));
    delete e.error.Response;
  }

  // Http error indication
  e.isHttpErrorResponse = e instanceof HttpErrorResponse;

  // GigyaResponse
  if (e.error?.errorCode) {
    e.error.isHttpErrorResponse = e.isHttpErrorResponse;
    return of(e.error);
  }

  // it's HttpErrorResponse
  // but we're adding GIGYA_RESPONSE_ERROR_PROPERTIES
  // to consolidate with GigyaResponse
  e.errorCode = e.status || 500;
  if (e.errorCode === 504) {
    e.errorMessage = 'Something went wrong';
    e.errorDetails = 'No response from the server';
  } else {
    e.errorMessage = e.statusText || e.errorDetails || e.errorMessage || 'General server error';
    e.errorDetails = e.error;
  }

  return of(e);
};

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private static generateUrlParams(params: object): HttpParams {
    let httpParams: HttpParams = new HttpParams({ encoder: new HttpParamsEncoder() });
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        httpParams = httpParams.set(key, params[key]);
      }
    }
    return httpParams;
  }

  constructor(private http: HttpClient,
              private sessionService: SessionService,
              private cookieService: CookieService,
              private dcEnvUtilsService: DcEnvUtilsService) {
  }

  getHeaders(contentType?: 'application/json' | string, customHeaders?: Record<string, any>): HttpHeaders {
    let headers = { 'X-XSRF-TOKEN': this.cookieService.get('XSRF-TOKEN') };
    if (customHeaders) {
      headers = Object.assign(headers, customHeaders);
    }
    if (contentType) {
      headers['Content-Type'] = contentType;
    }
    return new HttpHeaders(headers);
  }

  // noinspection JSUnusedGlobalSymbols
  sendRequest<T>(endpoint: string, httpMethod: HttpMethod, paramsObj: ApiServiceParams = {}, baseUrl?, getRawResponse: boolean = false, shouldOverrideDCSettings: boolean = false): Observable<T> {

    const request$: Observable<T & GigyaResponse> = defer(() => {

      const options: RequestOptions = {
        withCredentials: true
      };

      if (getRawResponse) {
        options.observe = 'response';
      }

      switch (httpMethod) {
        case 'DELETE':
        case 'GET':
        case 'HEAD':
        case 'OPTIONS':
          options.headers = this.getHeaders(null, paramsObj.requestHeader);
          delete paramsObj.requestHeader;
          options.params = ApiService.generateUrlParams(paramsObj.queryParams ?? paramsObj);
          break;

        default:
          // POST / PUT
          options.headers = this.getHeaders('application/json', paramsObj.requestHeader);
          if(paramsObj.queryParams) {
            options.params = ApiService.generateUrlParams(paramsObj.queryParams);
            delete paramsObj.queryParams;
          }
          options.body = paramsObj.requestBody ? paramsObj.requestBody : paramsObj;
          break;
      }

      let finalURL: string;
      return this.dcEnvUtilsService.businessUnitDc$.pipe(filter(dc => shouldOverrideDCSettings || !!dc),
        take(1),
        tap(() => {
          if (!finalURL) {
            baseUrl = baseUrl ? baseUrl : this.dcEnvUtilsService.getAPIBaseURL();
            finalURL = `${baseUrl}/${endpoint}`;
          }
        }),
        mergeMap(() => this.http.request<T & GigyaResponse>(httpMethod, finalURL, options as any)),
        catchError(e => errorHandler(e)),
        shareReplay({bufferSize: 1, refCount: true}));
    });

    return this._sendRequestInner<T & GigyaResponse>(request$)
      .pipe(
        switchMap(res =>
          (res as any)?.isHttpErrorResponse // error was logged before by errorHandler so we can just rethrow the error
            ? throwError(res as GigyaResponse) as never
            : !res?.errorCode // errorCode doesn't exist for successful REST api response
              ? of(res as T)
              : errorHandler(res).pipe(
                  switchMap(err =>
                    throwError(err as GigyaResponse) as never))));
  }

  private _sendRequestInner<T>(request$: Observable<T>): Observable<T> {

    // refresh session
    const refreshSession$ = defer(async () =>
      await this.sessionService.refreshSession());

    // make sure session is valid then send the request
    return refreshSession$
      .pipe(catchError(e => throwError(e)))
      .pipe(mapTo(request$))
      .pipe(concatAll());
  }
}
